/**
 * @fileoverview Hook for fetching and managing feature flag values from the API.
 * Feature flags allow dynamic enabling/disabling of features without code changes.
 *
 * @example
 * // Get a boolean feature flag
 * const isFeatureEnabled = useFeatureFlag<boolean>('my-feature');
 *
 * @example
 * // Get a feature flag with a complex value
 * const featureConfig = useFeatureFlag<{enabled: boolean, value: string}>('feature-config');
 */

import axios from 'axios'
import { useMemo, useState } from 'react'

/**
 * @function useFeatureFlag
 * @description Hook for fetching and managing feature flag values from the API.
 * @param {string} name - The name of the feature flag to fetch.
 * @returns {T | undefined} The value of the feature flag, or undefined if not found.
 */
export function useFeatureFlag<T>(name: string) {
    const [state, setState] = useState<T>(undefined)

    useMemo(() => {
        if (!name) {
            return
        }

        axios
            .get(`${process.env.API_HOST}/featureFlags/${name}`)
            .then(({ data }) => setState(data))
            .catch(() => {
                // noop
            })
    }, [name])

    return state
}
